/**
 * collection-oos-sort.js v2
 *
 * Garante que produtos out-of-stock (OOS) apareçam nas últimas páginas da paginação.
 *
 * PROBLEMA:
 *   O Shopify pagina collection.products no servidor antes do Liquid ser renderizado.
 *   O Liquid já faz dois loops (disponíveis primeiro, OOS depois) dentro de cada página
 *   do servidor, mas não pode mover produtos entre páginas.
 *
 * SOLUÇÃO:
 *   1. Busca todos os produtos via /collections/{handle}/products.json para obter
 *      disponibilidade e ordem original.
 *   2. Calcula a "ordem virtual correta": todos disponíveis primeiro, depois OOS.
 *   3. Para a página atual, determina quais produtos deveriam estar aqui.
 *   4. Produtos em falta (que estão em outras páginas do servidor) são buscados
 *      via AJAX varrendo todas as páginas do servidor até encontrá-los.
 *   5. O DOM é reconstruído com os produtos corretos na ordem correta.
 */

(function () {
  'use strict';

  // ── Logging ──────────────────────────────────────────────────────────────────

  function log() {
    if (window._oosDebug) {
      console.log.apply(console, ['[OOS-Sort]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // ── Meta ─────────────────────────────────────────────────────────────────────

  function getMeta() {
    var el = document.getElementById('collection-oos-meta');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || el.innerText || '');
    } catch (e) {
      return null;
    }
  }

  // ── Products JSON API ────────────────────────────────────────────────────────

  function fetchProductsPage(handle, page) {
    var url = '/collections/' + encodeURIComponent(handle)
              + '/products.json?limit=250&page=' + page;
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function fetchAllProducts(handle) {
    var all = [];
    function next(page) {
      return fetchProductsPage(handle, page).then(function (data) {
        var batch = (data && data.products) ? data.products : [];
        all = all.concat(batch);
        return batch.length === 250 ? next(page + 1) : all;
      });
    }
    return next(1);
  }

  function isAvailable(product) {
    return (product.variants || []).some(function (v) { return v.available; });
  }

  // Tag filtering (client-side, para coleções com filtro de tag na URL)
  function filterByTags(products, tags) {
    if (!tags || !tags.length) return products;
    return products.filter(function (p) {
      var ptags = p.tags || [];
      if (typeof ptags === 'string') {
        ptags = ptags.split(',').map(function (t) { return t.trim(); });
      }
      return tags.every(function (tag) { return ptags.indexOf(tag) !== -1; });
    });
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────────

  function getGridEl() {
    // Tenta grid view primeiro, depois list view
    return (
      document.querySelector('.product-grid-view .row') ||
      document.querySelector('.product-list-view')
    );
  }

  function readDomCards(grid) {
    // Use first-match-wins: o wrapper externo (col-md-4) tem data-product-handle
    // e vem antes do div interno (engoj_grid_parent) que também tem o mesmo atributo.
    // Pegar o elemento externo garante que o Bootstrap grid não quebre ao mover cards.
    var cards = [];
    var seen = {};
    Array.prototype.forEach.call(
      grid.querySelectorAll('[data-product-handle]'),
      function (el) {
        var h = el.getAttribute('data-product-handle');
        if (h && !seen[h]) {
          seen[h] = true;
          cards.push({
            el: el,
            handle: h,
            available: el.getAttribute('data-available') === 'true',
          });
        }
      }
    );
    return cards;
  }

  // ── Filtros de vitrine ────────────────────────────────────────────────────────

  /**
   * Filtros nativos da Shopify (?filter.*) e busca (?q=) reduzem o conjunto de
   * produtos da coleção, mas /products.json sempre devolve a coleção INTEIRA.
   * Se a ordem virtual fosse calculada sobre a coleção inteira, os produtos
   * filtrados seriam removidos do grid e substituídos por produtos que a busca
   * filtrada não devolve — o grid apareceria correto e logo depois esvaziaria.
   * Com filtro ativo, portanto, só reordenamos o que já está no DOM.
   */
  function hasStorefrontFilter() {
    return /[?&](filter\.|q=)/.test(window.location.search || '');
  }

  // ── URL builder ───────────────────────────────────────────────────────────────

  function buildCollectionPageUrl(serverPage) {
    // Remove ?page=N atual e adiciona a nova
    var search = window.location.search
      .replace(/([?&])page=\d+(&|$)/, function (m, pre, post) {
        return post ? pre : '';
      })
      .replace(/^\?$/, '');

    if (serverPage > 1) {
      var sep = search ? '&' : '?';
      return window.location.pathname + search + sep + 'page=' + serverPage;
    }
    return window.location.pathname + search;
  }

  // ── Busca cards HTML de uma página do servidor ────────────────────────────────

  function fetchServerPageCards(serverPage) {
    var url = buildCollectionPageUrl(serverPage);
    log('AJAX server page', serverPage, '→', url);
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var map = {};
        Array.prototype.forEach.call(
          doc.querySelectorAll('[data-product-handle]'),
          function (el) {
            var h = el.getAttribute('data-product-handle');
            if (h && !map[h]) map[h] = el;
          }
        );
        return map;
      });
  }

  /**
   * Busca todos os cards necessários varrendo páginas do servidor.
   * Começa na página 1 e vai até maxServerPage ou até encontrar tudo.
   *
   * Isso é necessário porque produtos OOS do servidor page 1 podem precisar
   * aparecer em páginas virtuais posteriores (ex: virtual page 2 exige os OOS
   * que estavam no servidor page 1).
   */
  function fetchAllNeededCards(neededHandles, maxServerPage, currentServerPage) {
    var found = {};
    var remaining = neededHandles.slice(); // cópia
    var pagesToFetch = [];

    // Monta lista de páginas para varrer: começa pela atual+1, depois página 1,
    // depois as demais em ordem
    for (var p = currentServerPage + 1; p <= maxServerPage; p++) {
      pagesToFetch.push(p);
    }
    // Também inclui páginas anteriores à atual (para OOS que ficaram em páginas anteriores)
    for (var p2 = 1; p2 < currentServerPage; p2++) {
      pagesToFetch.push(p2);
    }

    function next(idx) {
      // Para quando encontrou tudo ou varreu todas as páginas
      var stillMissing = remaining.filter(function (h) { return !found[h]; });
      if (!stillMissing.length || idx >= pagesToFetch.length) {
        return Promise.resolve(found);
      }

      var serverPage = pagesToFetch[idx];
      return fetchServerPageCards(serverPage)
        .then(function (cards) {
          stillMissing.forEach(function (h) {
            if (cards[h]) found[h] = cards[h];
          });
          return next(idx + 1);
        })
        .catch(function (err) {
          log('Erro ao buscar página', serverPage, ':', err.message);
          return next(idx + 1);
        });
    }

    return next(0);
  }

  // ── Re-init features ──────────────────────────────────────────────────────────

  function reinitFeatures() {
    // Lazy loading de imagens
    if (window.lazySizes) {
      var imgs = document.querySelectorAll('[data-product-handle] img[data-src]');
      Array.prototype.forEach.call(imgs, function (img) {
        if (!img.classList.contains('lazyloaded')) {
          img.classList.add('lazyload');
        }
      });
    }

    // Quick view (Shopify theme)
    if (typeof window.quickView === 'function') {
      try { window.quickView(); } catch (e) {}
    }

    // Ajax cart
    if (typeof window.ajaxCart !== 'undefined' && typeof window.ajaxCart.init === 'function') {
      try {
        window.ajaxCart.init({
          cartContainer: '.enj-minicart-ajax',
          addToCartSelector: '.enj-add-to-cart-btn',
          cartCountSelector: '.enj-cartcount',
          cartCostSelector: '.enj-cartcost',
          moneyFormat: window._shopMoneyFormat || '',
        });
      } catch (e) {}
    }

    // Seletor de cor Engoj
    if (typeof jQuery !== 'undefined') {
      jQuery('.engoj_select_color a').off('click.oossort').on('click.oossort', function () {
        var img = jQuery(this).data('engojvariant-img');
        jQuery(this).closest('.engoj_grid_parent').find('.engoj_find_img img').attr('src', img);
        return false;
      });
    }
  }

  // ── Fallback: reordena só o DOM atual ─────────────────────────────────────────

  function fallbackReorder(grid) {
    var cards = readDomCards(grid);
    var avail = cards.filter(function (c) { return c.available; });
    var oos = cards.filter(function (c) { return !c.available; });
    avail.concat(oos).forEach(function (c) { grid.appendChild(c.el); });
    log('Fallback: reordenação DOM local aplicada.');
  }

  // ── Lógica principal ──────────────────────────────────────────────────────────

  function run(meta) {
    var handle      = meta.handle;
    var currentPage = parseInt(meta.currentPage, 10) || 1;
    var perPage     = parseInt(meta.perPage, 10) || 42;
    var totalPages  = parseInt(meta.totalPages, 10) || 1;
    var currentTags = meta.currentTags || [];
    var sortBy      = meta.sortBy || '';

    log('handle:', handle, '| page:', currentPage, '| perPage:', perPage,
        '| totalPages:', totalPages, '| tags:', currentTags, '| sortBy:', sortBy);

    var grid = getGridEl();
    if (!grid) { log('Grid não encontrado.'); return; }

    var domCards = readDomCards(grid);
    if (!domCards.length) { log('Nenhum card no DOM.'); return; }

    var domHandles = domCards.map(function (c) { return c.handle; });
    var domCardsByHandle = {};
    domCards.forEach(function (c) { domCardsByHandle[c.handle] = c; });

    // Filtro de vitrine ativo — apenas reordena o DOM local
    if (hasStorefrontFilter()) {
      log('Filtro de vitrine ativo — reordenação DOM local apenas.');
      fallbackReorder(grid);
      return;
    }

    // Se sort_by ativo (usuário escolheu ordenação diferente da padrão),
    // apenas reordena DOM local — respeita a escolha do usuário
    if (sortBy && sortBy !== '' && sortBy !== 'manual') {
      log('sort_by ativo — reordenação DOM local apenas.');
      fallbackReorder(grid);
      return;
    }

    // Busca todos os produtos da coleção via Products JSON API
    fetchAllProducts(handle)
      .then(function (allProducts) {
        log('Produtos da API:', allProducts.length);

        // Filtra por tags ativas (se houver)
        var filtered = filterByTags(allProducts, currentTags);
        log('Após filtro de tags:', filtered.length);

        // Separa disponíveis e OOS na ordem original da API
        var available = filtered.filter(isAvailable);
        var oos       = filtered.filter(function (p) { return !isAvailable(p); });
        log('Disponíveis:', available.length, '| OOS:', oos.length);

        // Ordem virtual: todos disponíveis primeiro, depois OOS
        var sorted = available.concat(oos);

        // Produtos esperados nesta página virtual
        var startIdx     = (currentPage - 1) * perPage;
        var pageProducts = sorted.slice(startIdx, startIdx + perPage);
        var expectedHandles = pageProducts.map(function (p) { return p.handle; });

        log('Esperados nesta página:', expectedHandles.length,
            '| primeiros:', expectedHandles.slice(0, 3));

        // DOM já está correto?
        if (arraysEqual(domHandles, expectedHandles)) {
          log('DOM já está na ordem correta. Nenhuma ação necessária.');
          return;
        }

        // Produtos que precisamos mas não estão no DOM
        var neededHandles = expectedHandles.filter(function (h) {
          return !domCardsByHandle.hasOwnProperty(h);
        });

        log('Produtos faltando no DOM:', neededHandles.length,
            neededHandles.length ? neededHandles.slice(0, 3) : '');

        if (!neededHandles.length) {
          // Todos os produtos já estão no DOM, só precisa reordenar
          log('Reordenando DOM...');
          expectedHandles.forEach(function (h) {
            if (domCardsByHandle[h]) grid.appendChild(domCardsByHandle[h].el);
          });
          log('DOM reordenado.');
          return;
        }

        // Busca os produtos faltando em outras páginas do servidor.
        // Varre todas as páginas (posteriores E anteriores à atual) porque:
        // - Produtos disponíveis normalmente estão em páginas posteriores
        // - Produtos OOS que precisam aparecer em páginas virtuais posteriores
        //   podem estar em páginas do servidor anteriores
        fetchAllNeededCards(neededHandles, totalPages, currentPage)
          .then(function (fetchedCards) {
            var foundCount = Object.keys(fetchedCards).length;
            log('Encontrados via AJAX:', foundCount, 'de', neededHandles.length);

            // Remove todos os cards atuais do DOM
            domCards.forEach(function (c) {
              if (c.el.parentNode === grid) grid.removeChild(c.el);
            });

            // Insere na ordem correta
            var inserted = 0;
            expectedHandles.forEach(function (h) {
              var card = domCardsByHandle[h]
                ? domCardsByHandle[h].el
                : fetchedCards[h] || null;
              if (card) {
                grid.appendChild(card);
                inserted++;
              } else {
                log('AVISO: card não encontrado para handle:', h);
              }
            });

            log('Produtos inseridos:', inserted, '/', expectedHandles.length);
            reinitFeatures();
          })
          .catch(function (err) {
            log('Erro no fetchAllNeededCards:', err.message);
            fallbackReorder(grid);
          });
      })
      .catch(function (err) {
        log('Erro ao buscar Products JSON:', err.message);
        fallbackReorder(grid);
      });
  }

  function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  // ── Ponto de entrada ──────────────────────────────────────────────────────────

  function init() {
    var meta = getMeta();
    if (!meta || !meta.handle) {
      log('Meta não encontrado ou handle ausente — script desativado.');
      return;
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { run(meta); });
    } else {
      run(meta);
    }
  }

  init();

  // API pública para re-execução após filtros/sorting via AJAX
  window.CollectionOosSort = {
    run: function () {
      var meta = getMeta();
      if (meta) run(meta);
    },
    fallbackReorder: fallbackReorder,
  };

})();
